<script setup>
import { computed, inject, provide, ref } from 'vue'
import Sprite from '@/__core__/components/sprite/Sprite.vue'
import ResultHeader from '@/__v1.5__/components/result-headers/ResultHeader.vue'
import useFetch from '@/__core__/composables/useFetch'
import { useLiveScoreboardStore } from '@/widgets/livescore/LiveScoreboard.store'
import { useDataRefresh } from '@/__core__/data-refresh/useDataRefresh'
import PopupContainer from '@/__v1.5__/components/popups/PopupContainer.vue'

const drfId = 'event-popup'

const props = defineProps({
  liveScoreboardProps: Object,
  event: {
    type: Object,
    default: () => null
  }
})

const liveScoreboardStore = useLiveScoreboardStore()
const { startRefresh } = useDataRefresh(60 * 1000, drfId)

const emit = defineEmits(['popup-close'])

const widgetWidth = inject('width', ref(0))

const { status } = useFetch(async () => {
  await liveScoreboardStore.fetchLiveScoreboardData(props.liveScoreboardProps)
  const drfHandlers = getDRFHandlers()

  startRefresh(drfHandlers.refreshFn, drfHandlers.shouldCallApi, drfHandlers.shouldStopRefresh)
})

const isLoading = computed(() => status.value === 'loading')
const resultHeaderWrapperWidth = computed(() => (widgetWidth.value > 360 ? 360 : widgetWidth.value - 20))
const resultHeaderProps = computed(() => {
  let scorers = liveScoreboardStore.scorers

  if (isLoading.value) {
    const [homeScore, awayScore] = props.event.teams.map(team => team.score)
    scorers = Array.from({ length: Math.max(homeScore, awayScore) }).map((_, index) => ({ id: index + 1, home: index < homeScore, away: index < awayScore }))
  }

  return {
    width: resultHeaderWrapperWidth.value,
    header: liveScoreboardStore.header,
    hasShadow: false,
    hasScorersSkeleton: true,
    scorers
  }
})

function closePopup() {
  !isLoading.value && emit('popup-close')
}

function getDRFHandlers() {
  const refreshFn = async () => liveScoreboardStore.scheduleLiveScoreboardRefresh(props.liveScoreboardProps)
  const shouldCallApi = () => liveScoreboardStore.getShouldCallApi()
  const shouldStopRefresh = () => liveScoreboardStore.getShouldStopRefresh

  return { refreshFn, shouldCallApi, shouldStopRefresh }
}

provide('status', status)
</script>

<template>
  <popup-container content-classes="szw-relative szw-max-w-[360px] szw-p-2.5 szw-w-full" @popup-close="closePopup" :drf-id="drfId">
    <div class="szw-h-fit szw-w-full szw-rounded-2 szw-bg-background">
      <button
        v-if="!isLoading"
        @click="closePopup"
        class="szw-absolute szw-right-2 szw-top-2 szw-h-8 szw-w-8 szw-flex szw-justify-center szw-items-center szw-bg-transparent szw-z-50"
        style="margin-left: auto"
        e2e-id="event-popup-close"
      >
        <sprite sprite-id="latest-close" />
      </button>

      <result-header v-bind="resultHeaderProps" />
    </div>
  </popup-container>
</template>
